import React from "react"
import { Switch, Route, Redirect } from "react-router-dom"

import ErrorBoundary from "../utils/error_boundary"
import ProtectedRoute from "./protected_route"


const retry = (fn, totalRetries = 7, retriesDone = 0, interval = 500) => {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error) => {
        setTimeout(() => {
          if (totalRetries === retriesDone) {
            reject(error)
            return
          }
          retry(fn, retriesDone + 1, interval).then(resolve, reject)
        }, (retriesDone + 1) * interval)
      })
  })
}

const LoadableHome = React.lazy(() => retry(() => import("../pages/home")))
const Home = props => <ErrorBoundary><LoadableHome {...props} /></ErrorBoundary>
const LoadableLogin = React.lazy(() => retry(() => import("../pages/login")))
const Login = props => <ErrorBoundary><LoadableLogin {...props} /></ErrorBoundary>
const LoadableSignup = React.lazy(() => retry(() => import("../pages/signup")))
const Signup = props => <ErrorBoundary><LoadableSignup {...props} /></ErrorBoundary>
const LoadableAdmin = React.lazy(() => retry(() => import("../pages/admin")))
const Admin = props => <ErrorBoundary><LoadableAdmin {...props} /></ErrorBoundary>
const LoadableLab = React.lazy(() => retry(() => import("../pages/lab")))
const Lab = props => <ErrorBoundary><LoadableLab {...props} /></ErrorBoundary>
const LoadableMedicalFiles = React.lazy(() => retry(() => import("../pages/medical_files")))
const MedicalFiles = props => <ErrorBoundary><LoadableMedicalFiles {...props} /></ErrorBoundary>
const LoadableSurveyResponse = React.lazy(() => retry(() => import("../pages/survey_response")))
const SurveyResponse = props => <ErrorBoundary><LoadableSurveyResponse {...props} /></ErrorBoundary>
const LoadableTfa = React.lazy(() => retry(() => import("../pages/tfa")))
const Tfa = props => <ErrorBoundary><LoadableTfa {...props} /></ErrorBoundary>
const LoadableChoosePassword = React.lazy(() => retry(() => import("../pages/choose_password")))
const ChoosePassword = props => <ErrorBoundary><LoadableChoosePassword {...props} /></ErrorBoundary>
const LoadablePassword = React.lazy(() => retry(() => import("../pages/password")))
const Password = props => <ErrorBoundary><LoadablePassword {...props} /></ErrorBoundary>
const LoadableEditDetails = React.lazy(() => retry(() => import("../pages/edit_details")))
const EditDetails = props => <ErrorBoundary><LoadableEditDetails {...props} /></ErrorBoundary>
const LoadableReceiveDataAndSupport = React.lazy(() => retry(() => import("../pages/receive_data_and_support")))
const ReceiveDataAndSupport = props => <ErrorBoundary><LoadableReceiveDataAndSupport {...props} /></ErrorBoundary>
const LoadableUseSupportedBrowser = React.lazy(() => retry(() => import("../pages/use_supported_browser")))
const UseSupportedBrowser = props => <ErrorBoundary><LoadableUseSupportedBrowser {...props} /></ErrorBoundary>
const LoadableCreateMissingFood = React.lazy(() => retry(() => import("../pages/create_missing_food")))
const CreateMissingFood = props => <ErrorBoundary><LoadableCreateMissingFood {...props} /></ErrorBoundary>
const LoadableMedications = React.lazy(() => retry(() => import("../pages/medications")))
const Medications = props => <ErrorBoundary><LoadableMedications {...props} /></ErrorBoundary>
const LoadableTeamTor4You = React.lazy(() => retry(() => import("../pages/team_tor4you_management")))
const TeamTor4YouManagement = props => <ErrorBoundary><LoadableTeamTor4You {...props} /></ErrorBoundary>

const LoadableEditFood = React.lazy(() => retry(() => import("../pages/edit_food")))
const EditFood = props => <ErrorBoundary><LoadableEditFood {...props} /></ErrorBoundary>

const LoadableCohorts = React.lazy(() => retry(() => import("../pages/cohort")))
const Cohorts = props => <ErrorBoundary><LoadableCohorts {...props} /></ErrorBoundary>

const LoadableSupport = React.lazy(() => retry(() => import("../pages/support")))
const Support = props => <ErrorBoundary><LoadableSupport {...props} /></ErrorBoundary>

const LoadableFoods = React.lazy(() => retry(() => import("../pages/foods")))
const Foods = props => <ErrorBoundary><LoadableFoods {...props} /></ErrorBoundary>


const LoadableCallCenter = React.lazy(() => retry(() => import("../pages/calls")))
const CallCenter = props => <ErrorBoundary><LoadableCallCenter {...props} /></ErrorBoundary>
const LoadableCall = React.lazy(() => retry(() => import("../pages/call")))
const Call = props => <ErrorBoundary><LoadableCall {...props} /></ErrorBoundary>



const Router = () => (
  <Switch>
    <Route exact path="/use-supported-browser" component={UseSupportedBrowser} />
    <Route exact path="/signup" component={Signup} />
    <Route exact path="/login" component={Login} />
    <Route exact path="/tfa" component={Tfa} />
    <Route exact path="/password" component={Password} />
    <Route exact path="/choose-password" component={ChoosePassword} />
    <ProtectedRoute exact path="/medical-files/:fileType/:labParticipantId" component={MedicalFiles} />
    <ProtectedRoute exact path="/user-blood-tests" component={() => <Redirect to="/medical-files/blood-tests" />} />
    <Route exact path="/survey-response" component={SurveyResponse} />
    <ProtectedRoute exact path="/calls/:participantId" component={Call} />
    <ProtectedRoute exact path="/calls" component={CallCenter} />
    <ProtectedRoute exact path="/admin" component={Admin} />
    <ProtectedRoute exact path="/lab/:participantId" component={Lab} />
    <ProtectedRoute exact path="/lab" component={Lab} />
    <ProtectedRoute exact path="/receive-data-and-support/:participantId" component={ReceiveDataAndSupport} />
    <ProtectedRoute exact path="/receive-data-and-support" component={ReceiveDataAndSupport} />
    <ProtectedRoute exact path="/missing-foods/create-food" component={CreateMissingFood} />
    <ProtectedRoute exact path="/edit-details" component={EditDetails} />
    <ProtectedRoute exact path="/medications" component={Medications} />
    <ProtectedRoute exact path="/foods" component={Foods} />
    <ProtectedRoute exact path="/editing-foods/:foodId" component={EditFood} />
    <ProtectedRoute exact path="/cohorts" component={Cohorts} />
    <ProtectedRoute path="/cohorts/schedule/:participantId" component={TeamTor4YouManagement} />
    <ProtectedRoute exact path="/support" component={Support} />
    <ProtectedRoute path="/home" component={Home} />
    <Route path="*" render={() => <Redirect to="/home" />} />
  </Switch>
)

export default Router
